"use client";

import * as React from "react";
import { FlowFieldSim, mulberry32 } from "./flowfield/simulation";
import { SwarmRenderer, Palette } from "./flowfield/renderer";
import { useIsDark, usePrefersReducedMotion, useSize } from "../../hooks";

const COUNT_PRESETS = [1000, 4000, 10000];
const TURRET_PRESETS = [10, 50, 100, 200];
const TARGET_COLS = 132;
const MAX_AGENTS = 10000;

type Stats = {
  fps: number;
  simMs: number;
  fieldMs: number;
  cells: number;
  kps: number;
  shots: number;
};

/** A settled reading at one agent count. This is what makes the demo a test. */
type Sample = { count: number; fieldMs: number; simMs: number; fps: number };

const DARK: Palette = {
  a: [0.35, 0.64, 1.0],
  b: [0.62, 0.83, 1.0],
  alpha: 0.92,
};
const LIGHT: Palette = {
  a: [0.04, 0.29, 0.7],
  b: [0.25, 0.52, 0.92],
  alpha: 0.86,
};

/**
 * The load-bearing demo. Real Dijkstra flow field, real spatial-hash
 * separation, one instanced draw call for the entire crowd.
 */
export default function FlowFieldSwarm() {
  const [wrapRef, size] = useSize<HTMLDivElement>();
  const glCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const overlayRef = React.useRef<HTMLCanvasElement | null>(null);

  const isDark = useIsDark();
  const reducedMotion = usePrefersReducedMotion();

  const [count, setCount] = React.useState(() =>
    typeof window !== "undefined" && window.innerWidth < 820 ? 4000 : 10000,
  );
  const [turretCount, setTurretCount] = React.useState(50);
  const [showField, setShowField] = React.useState(false);
  const [sprites, setSprites] = React.useState(true);
  const [running, setRunning] = React.useState(true);
  const [unsupported, setUnsupported] = React.useState(false);
  const [samples, setSamples] = React.useState<Record<number, Sample>>({});
  const [stats, setStats] = React.useState<Stats>({
    fps: 0,
    simMs: 0,
    fieldMs: 0,
    cells: 0,
    kps: 0,
    shots: 0,
  });

  // Mutable frame state — deliberately outside React so the loop never
  // triggers a render.
  const simRef = React.useRef<FlowFieldSim | null>(null);
  const rendererRef = React.useRef<SwarmRenderer | null>(null);
  const glRef = React.useRef<WebGL2RenderingContext | null>(null);
  const goalRef = React.useRef({ x: 0, y: 0, pointer: false });
  const paletteRef = React.useRef<Palette>(DARK);
  const runningRef = React.useRef(true);
  const showFieldRef = React.useRef(false);
  const spritesRef = React.useRef(true);
  const visibleRef = React.useRef(true);
  // Readings are only recorded once the sim has run at a count long enough to
  // settle, so a mid-transition frame never lands in the table.
  const countRef = React.useRef(count);
  const settledAtRef = React.useRef(0);

  paletteRef.current = isDark ? DARK : LIGHT;
  runningRef.current = running && !reducedMotion;
  showFieldRef.current = showField;
  spritesRef.current = sprites;

  const width = size.width;
  const height = size.height;

  // ---- build / rebuild the simulation whenever the box changes -------------
  React.useEffect(() => {
    if (width < 200 || height < 160) return;

    const cellSize = width / TARGET_COLS;
    const cols = TARGET_COLS;
    const rows = Math.max(24, Math.round(height / cellSize));

    const sim = new FlowFieldSim({
      cols,
      rows,
      cellSize,
      maxAgents: MAX_AGENTS,
    });
    layoutObstacles(sim);
    placeTurrets(sim, turretCount);
    sim.populate(count, mulberry32(0x5eed));
    sim.rebuildField(sim.worldWidth * 0.5, sim.worldHeight * 0.5);

    simRef.current = sim;
    goalRef.current = {
      x: sim.worldWidth * 0.5,
      y: sim.worldHeight * 0.5,
      pointer: false,
    };
    setStats((s) => ({ ...s, cells: cols * rows }));

    return () => {
      simRef.current = null;
    };
    // `count` is applied separately so resizing does not reset the crowd.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  // ---- apply agent-count changes without rebuilding the grid ---------------
  React.useEffect(() => {
    const sim = simRef.current;
    if (!sim) return;
    sim.populate(count, mulberry32(0xc0ffee));
    countRef.current = count;
    settledAtRef.current = performance.now() + 2200;
  }, [count, width, height]);

  // ---- turret count, without disturbing the grid or the crowd -------------
  React.useEffect(() => {
    const sim = simRef.current;
    if (!sim) return;
    placeTurrets(sim, turretCount);
  }, [turretCount, width, height]);

  // ---- GL context ----------------------------------------------------------
  React.useEffect(() => {
    const canvas = glCanvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl2", {
      alpha: true,
      antialias: false,
      premultipliedAlpha: false,
      powerPreference: "high-performance",
    });

    if (!gl) {
      setUnsupported(true);
      return;
    }

    let renderer: SwarmRenderer;
    try {
      renderer = new SwarmRenderer(gl, MAX_AGENTS);
    } catch (err) {
      // Worth surfacing: a shader failure here is a bug, not an unsupported
      // browser, and the two look identical from the outside.
      console.error("[swarm] renderer init failed", err);
      setUnsupported(true);
      return;
    }

    glRef.current = gl;
    rendererRef.current = renderer;

    // The game's own enemy art, four variants in one sheet.
    const img = new Image();
    img.decoding = "async";
    img.onload = () => renderer.setAtlas(img);
    img.src = "/media/swarm-agents.png";

    return () => {
      renderer.dispose();
      rendererRef.current = null;
      glRef.current = null;
    };
  }, []);

  // ---- pause when off-screen ----------------------------------------------
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.05 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [wrapRef]);

  // ---- the frame loop ------------------------------------------------------
  React.useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    let frames = 0;
    let simAcc = 0;
    let fieldAcc = 0;
    let fieldN = 0;
    let orbit = 0;
    let killMark = 0;

    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);

      const sim = simRef.current;
      const gl = glRef.current;
      const renderer = rendererRef.current;
      const canvas = glCanvasRef.current;
      if (!sim || !canvas) return;

      const dtRaw = (now - last) / 1000;
      last = now;
      if (!visibleRef.current) return;

      const dt = Math.min(dtRaw, 1 / 30);

      // Goal: the pointer while it is over the canvas, a slow orbit otherwise.
      const goal = goalRef.current;
      if (!goal.pointer) {
        orbit += dt * 0.24;
        const rx = sim.worldWidth * 0.27;
        const ry = sim.worldHeight * 0.24;
        goal.x = sim.worldWidth * 0.5 + Math.cos(orbit) * rx;
        goal.y = sim.worldHeight * 0.5 + Math.sin(orbit * 1.31) * ry;
      }

      const t0 = performance.now();
      if (sim.rebuildField(goal.x, goal.y)) {
        fieldAcc += sim.lastFieldMs;
        fieldN += 1;
      }
      if (runningRef.current) sim.step(dt, sim.cellSize * 9, 0.32);
      const t1 = performance.now();

      // ---- draw ------------------------------------------------------------
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const pxW = Math.round(sim.worldWidth * dpr);
      const pxH = Math.round(sim.worldHeight * dpr);
      if (canvas.width !== pxW || canvas.height !== pxH) {
        canvas.width = pxW;
        canvas.height = pxH;
      }

      if (gl && renderer) {
        gl.viewport(0, 0, pxW, pxH);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        renderer.fill(sim.count, sim.px, sim.py, sim.vx, sim.vy, sim.tint);
        // Sprites need to be bigger than dots to read as characters, and the
        // smaller the crowd the more room there is to give them.
        const dot = Math.max(2.6, sim.cellSize * 0.62);
        const spriteScale = Math.min(
          3.6,
          Math.max(1.9, 3.4 * Math.pow(1000 / Math.max(sim.count, 1), 0.2)),
        );
        renderer.draw(
          sim.count,
          sim.worldWidth,
          sim.worldHeight,
          spritesRef.current ? dot * spriteScale : dot,
          paletteRef.current,
          spritesRef.current,
        );
      }

      drawOverlay(
        overlayRef.current,
        sim,
        goal,
        showFieldRef.current,
        isDark,
        dpr,
      );

      // ---- rolling readout -------------------------------------------------
      simAcc += t1 - t0;
      frames += 1;
      acc += dtRaw;
      if (acc >= 0.5) {
        setStats({
          fps: Math.round(frames / acc),
          simMs: +(simAcc / frames).toFixed(2),
          fieldMs: +(fieldN ? fieldAcc / fieldN : sim.lastFieldMs).toFixed(2),
          cells: sim.cols * sim.rows,
          kps: Math.round((sim.kills - killMark) / acc),
          shots: sim.shotCount,
        });
        // Record once the count has been stable for a couple of seconds.
        if (runningRef.current && now >= settledAtRef.current) {
          const reading: Sample = {
            count: countRef.current,
            fieldMs: +(fieldN ? fieldAcc / fieldN : sim.lastFieldMs).toFixed(2),
            simMs: +(simAcc / frames).toFixed(2),
            fps: Math.round(frames / acc),
          };
          setSamples((prev) =>
            prev[reading.count] &&
            prev[reading.count].fieldMs === reading.fieldMs &&
            prev[reading.count].simMs === reading.simMs
              ? prev
              : { ...prev, [reading.count]: reading },
          );
        }
        killMark = sim.kills;
        acc = 0;
        frames = 0;
        simAcc = 0;
        fieldAcc = 0;
        fieldN = 0;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isDark]);

  // ---- pointer -------------------------------------------------------------
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const sim = simRef.current;
    const el = wrapRef.current;
    if (!sim || !el) return;
    const rect = el.getBoundingClientRect();
    goalRef.current = {
      x: ((e.clientX - rect.left) / rect.width) * sim.worldWidth,
      y: ((e.clientY - rect.top) / rect.height) * sim.worldHeight,
      pointer: true,
    };
  };

  const onPointerLeave = () => {
    goalRef.current.pointer = false;
  };

  return (
    <div className="panel overflow-hidden">
      {/* ---- canvas stage ---- */}
      <div
        ref={wrapRef}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        className="relative aspect-[16/10] w-full touch-none bg-raised sm:aspect-[16/9]"
      >
        <canvas
          ref={overlayRef}
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden
        />
        <canvas
          ref={glCanvasRef}
          className="absolute inset-0 h-full w-full"
          role="img"
          aria-label={`Live simulation: ${count.toLocaleString()} agents navigating a Dijkstra flow field around obstacles toward a moving goal, while nine turrets acquire and shoot them down.`}
        />

        {unsupported && (
          <div className="absolute inset-0 grid place-items-center p-6 text-center">
            <p className="max-w-sm text-sm text-muted">
              This demo needs WebGL2, which your browser has turned off or does
              not support. The numbers below still describe what it does.
            </p>
          </div>
        )}

        {reducedMotion && !unsupported && (
          <div className="absolute inset-x-0 bottom-0 bg-surface/90 px-4 py-2 text-center text-2xs text-muted backdrop-blur">
            Paused — your system requests reduced motion. Press play to run it
            anyway.
          </div>
        )}

        {/* live readout */}
        <div className="pointer-events-none absolute left-3 top-3 flex flex-col gap-1 rounded-md border bg-bg/70 px-2.5 py-2 font-mono text-2xs leading-tight text-muted backdrop-blur-sm">
          <Readout label="agents" value={count.toLocaleString()} strong />
          <Readout label="fps" value={`${stats.fps} (display-capped)`} />
          <Readout
            label="budget"
            value={`${(stats.simMs + stats.fieldMs).toFixed(2)} / 16.67 ms`}
          />
          <Readout
            label="turrets"
            value={`${turretCount} · ${stats.kps}/s killed`}
          />
          <Readout label="in flight" value={String(stats.shots)} />
          {/* The detail lines cost more than they are worth on a phone. */}
          <Readout
            label="agents+turrets"
            value={`${stats.simMs.toFixed(2)} ms`}
            className="hidden sm:flex"
          />
          <Readout
            label="field"
            value={`${stats.fieldMs.toFixed(2)} ms / ${stats.cells.toLocaleString()} cells`}
            className="hidden sm:flex"
          />
          <Readout label="draw calls" value="1" className="hidden sm:flex" />
        </div>
      </div>

      {/* ---- controls ---- */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="eyebrow">Agents</span>
          <div className="seg" role="group" aria-label="Agent count">
            {COUNT_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                className="seg-item"
                aria-pressed={count === n}
                onClick={() => setCount(n)}
              >
                {n >= 1000 ? `${n / 1000}k` : n}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="eyebrow">Turrets</span>
          <div className="seg" role="group" aria-label="Turret count">
            {TURRET_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                className="seg-item"
                aria-pressed={turretCount === n}
                onClick={() => setTurretCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="seg">
          <button
            type="button"
            className="seg-item"
            aria-pressed={sprites}
            onClick={() => setSprites((v) => !v)}
          >
            Game art
          </button>
          <button
            type="button"
            className="seg-item"
            aria-pressed={showField}
            onClick={() => setShowField((v) => !v)}
          >
            Show field
          </button>
        </div>

        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setRunning((v) => !v)}
        >
          {running ? "Pause" : "Play"}
        </button>

        <p className="ml-auto hidden text-2xs text-faint sm:block">
          Move your cursor. The goal follows it and the field is rebuilt.
        </p>
      </div>

      <Evidence samples={samples} active={count} />
    </div>
  );
}

/**
 * The demo as an experiment rather than a decoration.
 *
 * Switching the agent count records a settled reading, so the reader builds
 * the evidence themselves. The claim being tested is narrow and checkable:
 * the field rebuild is the entire pathfinding cost for every agent on screen,
 * and it does not move when the population does.
 */
function Evidence({
  samples,
  active,
}: {
  samples: Record<number, Sample>;
  active: number;
}) {
  const rows = Object.values(samples).sort((a, b) => a.count - b.count);
  const untried = COUNT_PRESETS.filter((c) => !samples[c]);

  return (
    <div className="border-t px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="eyebrow">Measured on your machine</p>
        <p className="text-2xs text-faint">
          {untried.length
            ? `Switch to ${untried
                .map((c) => (c >= 1000 ? `${c / 1000}k` : c))
                .join(" and ")} to fill the table.`
            : "Field rebuild is the whole pathfinding cost, for every agent on screen."}
        </p>
      </div>

      <table className="mt-2.5 w-full max-w-lg border-separate border-spacing-0 font-mono text-2xs">
        <thead>
          <tr className="text-faint">
            <th className="py-1 text-left font-normal">agents</th>
            <th className="py-1 text-right font-normal">field rebuild</th>
            <th className="py-1 text-right font-normal">everything else</th>
            <th className="py-1 text-right font-normal">fps</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="py-1.5 text-faint">
                warming up…
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.count}
                className={r.count === active ? "text-ink" : "text-muted"}
              >
                <td className="border-t py-1.5">
                  {r.count.toLocaleString()}
                  {r.count === active && (
                    <span className="ml-1.5 text-accent">●</span>
                  )}
                </td>
                <td className="border-t py-1.5 text-right text-accent">
                  {r.fieldMs.toFixed(2)} ms
                </td>
                <td className="border-t py-1.5 text-right">
                  {r.simMs.toFixed(2)} ms
                </td>
                <td className="border-t py-1.5 text-right">{r.fps}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {rows.length > 1 && (
        <p className="mt-2 max-w-xl text-2xs leading-relaxed text-faint">
          The fps column is capped by your display&rsquo;s refresh rate, so 60
          means the frame budget held, not that it ran out of headroom.
        </p>
      )}
    </div>
  );
}

function Readout({
  label,
  value,
  strong,
  className = "flex",
}: {
  label: string;
  value: string;
  strong?: boolean;
  className?: string;
}) {
  return (
    <span className={`${className} gap-1.5`}>
      <span className="text-faint">{label}</span>
      <span className={strong ? "text-ink" : undefined}>{value}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ overlay */

/**
 * Static obstacle layer, rasterised once per (size, theme) and blitted after.
 * Painting it cell by cell every frame left visible seams where the half-pixel
 * overlaps composited, and cost a full grid walk for something that never
 * moves.
 */
const wallCache: {
  canvas: HTMLCanvasElement | null;
  key: string;
} = { canvas: null, key: "" };

function wallLayer(sim: FlowFieldSim, isDark: boolean, dpr: number) {
  const key = `${sim.cols}x${sim.rows}@${dpr}:${isDark}`;
  if (wallCache.canvas && wallCache.key === key) return wallCache.canvas;

  const c = wallCache.canvas ?? document.createElement("canvas");
  c.width = Math.round(sim.worldWidth * dpr);
  c.height = Math.round(sim.worldHeight * dpr);
  const ctx = c.getContext("2d");
  if (!ctx) return c;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, sim.worldWidth, sim.worldHeight);

  const { cols, rows, cellSize, walls } = sim;

  // Collect every row's solid run into ONE compound path and fill it once.
  // Filling rect by rect double-composites the half-pixel overlaps and leaves
  // visible seams through a translucent colour; a single fill cannot.
  ctx.beginPath();
  for (let y = 0; y < rows; y++) {
    let run = -1;
    for (let x = 0; x <= cols; x++) {
      const solid = x < cols && walls[y * cols + x] === 1;
      if (solid && run < 0) run = x;
      if (!solid && run >= 0) {
        ctx.rect(
          run * cellSize,
          y * cellSize,
          (x - run) * cellSize,
          cellSize + 0.6,
        );
        run = -1;
      }
    }
  }
  ctx.fillStyle = isDark ? "rgba(148,163,184,0.16)" : "rgba(17,19,23,0.11)";
  ctx.fill();

  wallCache.canvas = c;
  wallCache.key = key;
  return c;
}

/** Obstacles, the goal marker and (optionally) the flow vectors. 2D canvas. */
function drawOverlay(
  canvas: HTMLCanvasElement | null,
  sim: FlowFieldSim,
  goal: { x: number; y: number },
  showField: boolean,
  isDark: boolean,
  dpr: number,
) {
  if (!canvas) return;
  const pxW = Math.round(sim.worldWidth * dpr);
  const pxH = Math.round(sim.worldHeight * dpr);
  if (canvas.width !== pxW || canvas.height !== pxH) {
    canvas.width = pxW;
    canvas.height = pxH;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, pxW, pxH);
  ctx.drawImage(wallLayer(sim, isDark, dpr), 0, 0);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const { cols, rows, cellSize, walls, flow, cost } = sim;

  // Flow vectors, subsampled so the field reads instead of turning to mud.
  if (showField) {
    const stepN = 3;
    const len = cellSize * stepN * 0.62;
    ctx.lineWidth = 1;
    ctx.strokeStyle = isDark ? "rgba(240,169,59,0.55)" : "rgba(176,106,6,0.55)";
    ctx.beginPath();
    for (let y = 1; y < rows; y += stepN) {
      for (let x = 1; x < cols; x += stepN) {
        const i = y * cols + x;
        if (walls[i] === 1 || cost[i] > 1e6) continue;
        const o = i * 2;
        const dx = flow[o];
        const dy = flow[o + 1];
        if (dx === 0 && dy === 0) continue;
        const cx = x * cellSize + cellSize / 2;
        const cy = y * cellSize + cellSize / 2;
        ctx.moveTo(cx - (dx * len) / 2, cy - (dy * len) / 2);
        ctx.lineTo(cx + (dx * len) / 2, cy + (dy * len) / 2);
      }
    }
    ctx.stroke();
  }

  const hot = isDark ? "240,169,59" : "176,106,6";

  // Impact flashes — expanding rings where a shot connected.
  for (let i = 0; i < sim.impactCount; i++) {
    const o = i * 3;
    const t = sim.impacts[o + 2] / 0.35;
    const rr = cellSize * (0.6 + t * 2.4);
    ctx.strokeStyle = `rgba(${hot},${(1 - t) * 0.75})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(sim.impacts[o], sim.impacts[o + 1], rr, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Projectiles — short streaks along the direction of travel.
  if (sim.shotCount > 0) {
    const tail = cellSize * 1.9;
    ctx.strokeStyle = `rgba(${hot},0.95)`;
    ctx.lineWidth = Math.max(1.2, cellSize * 0.26);
    ctx.lineCap = "round";
    ctx.beginPath();
    for (let i = 0; i < sim.shotCount; i++) {
      const o = i * 5;
      const x = sim.shots[o];
      const y = sim.shots[o + 1];
      ctx.moveTo(x, y);
      ctx.lineTo(x - sim.shots[o + 2] * tail, y - sim.shots[o + 3] * tail);
    }
    ctx.stroke();
  }

  // Turrets. Batched into three paths rather than three ops per turret, so
  // two hundred of them cost the same handful of canvas calls as nine.
  // Emplacements shrink as their number grows. Two hundred at the ten-turret
  // size reads as noise rather than a defended field.
  const tr = Math.max(
    2,
    cellSize *
      Math.min(
        1.15,
        Math.max(0.4, 1.15 * Math.pow(10 / sim.turrets.length, 0.3)),
      ),
  );

  ctx.beginPath();
  for (const t of sim.turrets) {
    ctx.moveTo(t.x + tr, t.y);
    ctx.arc(t.x, t.y, tr, 0, Math.PI * 2);
  }
  ctx.fillStyle = isDark ? "rgba(226,232,240,0.22)" : "rgba(17,19,23,0.16)";
  ctx.fill();
  ctx.strokeStyle = isDark ? "rgba(226,232,240,0.6)" : "rgba(17,19,23,0.5)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Barrels, split into resting and just-fired so each pass is one stroke.
  ctx.lineWidth = Math.max(1, tr * 0.34);
  ctx.lineCap = "round";
  for (const pass of [0, 1]) {
    ctx.beginPath();
    let any = false;
    for (const t of sim.turrets) {
      const firing = t.sinceFire < 0.09;
      if ((pass === 1) !== firing) continue;
      any = true;
      const len = tr * (pass === 1 ? 2.7 : 2);
      ctx.moveTo(t.x, t.y);
      ctx.lineTo(t.x + Math.cos(t.angle) * len, t.y + Math.sin(t.angle) * len);
    }
    if (!any) continue;
    ctx.strokeStyle = `rgba(${hot},${pass === 1 ? 1 : 0.55})`;
    ctx.stroke();
  }

  // Goal — a crosshair, so it never reads as another turret.
  const r = Math.max(6, cellSize * 1.5);
  ctx.strokeStyle = `rgba(${hot},0.95)`;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(goal.x, goal.y, r, 0, Math.PI * 2);
  ctx.moveTo(goal.x - r * 1.8, goal.y);
  ctx.lineTo(goal.x - r * 0.55, goal.y);
  ctx.moveTo(goal.x + r * 0.55, goal.y);
  ctx.lineTo(goal.x + r * 1.8, goal.y);
  ctx.moveTo(goal.x, goal.y - r * 1.8);
  ctx.lineTo(goal.x, goal.y - r * 0.55);
  ctx.moveTo(goal.x, goal.y + r * 0.55);
  ctx.lineTo(goal.x, goal.y + r * 1.8);
  ctx.stroke();
}

/* ---------------------------------------------------------------- obstacles */

/**
 * Scatter `n` turrets over the open ground, deterministically.
 *
 * A jittered grid rather than pure random, so two hundred emplacements cover
 * the field evenly instead of clumping. Range shrinks as the count grows: at
 * two hundred turrets a twenty-cell radius each would have them all shooting
 * the same few agents, and acquisition would scan far more grid than it needs.
 */
function placeTurrets(sim: FlowFieldSim, n: number) {
  const { cols, rows, cellSize } = sim;
  sim.clearTurrets();

  const rangeCells = Math.max(7, 20 * Math.pow(10 / n, 0.28));
  const interval = 0.2;
  const rng = mulberry32(0xa11ce ^ n);

  const columns = Math.max(1, Math.round(Math.sqrt(n * (cols / rows))));
  const lanes = Math.max(1, Math.ceil(n / columns));
  let placed = 0;

  for (let ly = 0; ly < lanes && placed < n; ly++) {
    for (let lx = 0; lx < columns && placed < n; lx++) {
      // Jitter inside the cell so the grid does not read as a grid.
      const fx = (lx + 0.25 + rng() * 0.5) / columns;
      const fy = (ly + 0.25 + rng() * 0.5) / lanes;
      const cx = Math.min(cols - 2, Math.max(1, Math.round(fx * cols)));
      const cy = Math.min(rows - 2, Math.max(1, Math.round(fy * rows)));
      if (sim.walls[cy * cols + cx] === 1) continue;

      sim.addTurret(
        (cx + 0.5) * cellSize,
        (cy + 0.5) * cellSize,
        rangeCells * cellSize,
        interval,
      );
      placed++;
    }
  }
}

/**
 * A walled compound with staggered gates, plus outer baffles that break the
 * straight approach lanes. Agents have to route around and funnel through,
 * which is the only way the field is visibly doing anything.
 */
function layoutObstacles(sim: FlowFieldSim) {
  const { cols, rows } = sim;
  sim.clearWalls();

  const cx = cols / 2;
  const cy = rows / 2;
  const t = Math.max(2, Math.round(rows * 0.035));

  const halfW = Math.round(cols * 0.16);
  const halfH = Math.round(rows * 0.22);

  // Vertical walls, each split by one gate. The gates sit on opposite sides
  // of centre so there is never a straight line through the compound.
  const vRun = halfH * 0.72;
  const vOff = halfH * 0.5;
  sim.stampRect(cx - halfW, cy - vOff, t, vRun);
  sim.stampRect(cx - halfW, cy + vOff + vRun * 0.28, t, vRun * 0.6);
  sim.stampRect(cx + halfW, cy + vOff, t, vRun);
  sim.stampRect(cx + halfW, cy - vOff - vRun * 0.28, t, vRun * 0.6);

  // Horizontal walls, gates offset the other way.
  const hRun = halfW * 0.78;
  const hOff = halfW * 0.52;
  sim.stampRect(cx - hOff, cy - halfH, hRun, t);
  sim.stampRect(cx + hOff + hRun * 0.3, cy - halfH, hRun * 0.55, t);
  sim.stampRect(cx + hOff, cy + halfH, hRun, t);
  sim.stampRect(cx - hOff - hRun * 0.3, cy + halfH, hRun * 0.55, t);

  // Outer baffles — long, thin, and rotationally symmetric so no approach
  // direction is privileged.
  const bw = Math.round(cols * 0.15);
  const bh = Math.round(rows * 0.16);
  sim.stampRect(cols * 0.17, rows * 0.26, bw, t);
  sim.stampRect(cols * 0.83, rows * 0.74, bw, t);
  sim.stampRect(cols * 0.26, rows * 0.78, t, bh);
  sim.stampRect(cols * 0.74, rows * 0.22, t, bh);
  sim.stampRect(cols * 0.5, rows * 0.06, t, bh * 0.7);
  sim.stampRect(cols * 0.5, rows * 0.94, t, bh * 0.7);
  sim.stampRect(cols * 0.06, rows * 0.5, bw * 0.6, t);
  sim.stampRect(cols * 0.94, rows * 0.5, bw * 0.6, t);

  // Emplacements inside and around the compound. Ranges overlap so the
  // approach lanes are covered from more than one angle.
  const cell = sim.cellSize;
  sim.clearTurrets();
  const place = (fx: number, fy: number, rangeCells: number, rate: number) =>
    sim.addTurret(cols * fx * cell, rows * fy * cell, rangeCells * cell, rate);

  place(0.5, 0.5, 22, 0.18);
  place(0.38, 0.38, 18, 0.26);
  place(0.62, 0.38, 18, 0.26);
  place(0.38, 0.62, 18, 0.26);
  place(0.62, 0.62, 18, 0.26);
  place(0.2, 0.5, 16, 0.32);
  place(0.8, 0.5, 16, 0.32);
  place(0.5, 0.2, 16, 0.32);
  place(0.5, 0.8, 16, 0.32);
}
